import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from "@mui/material";
import { useMemo, useState } from "react";
import MediaGallery from "./MediaGallery";
import { useMediaScope } from "../context/PremiseScopeContext";
import type { AssetSummary, MediaLayer } from "../http/foyer";

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string, asset: AssetSummary) => void;
}

/**
 * Choose an image or clip from a layer this scope actually owns.
 *
 * Global media is NOT offered here when a location or customer is in scope.
 * It is Nenda's own shelf - almost entirely app and radio catalogue icons -
 * and a hotel picking a file out of it would be building its signage from
 * assets it does not control and cannot manage. The tab only appears where
 * there is nothing else in scope, which is the admin's own Global media page.
 */
export default function AssetsPicker({ open, onClose, onSelect }: Props) {
  const scope = useMediaScope();

  const tabs = useMemo(() => {
    const out: Array<{ key: string; label: string; layer: MediaLayer }> = [];
    if (scope.premiseId) {
      out.push({
        key: "premise",
        label: "This location",
        layer: { kind: "premise", premiseId: scope.premiseId },
      });
    }
    if (scope.companyId) {
      out.push({
        key: "company",
        label: "Company",
        layer: { kind: "company", companyId: scope.companyId },
      });
    }
    // Only when nothing else is in scope - see the note above.
    if (out.length === 0) {
      out.push({
        key: "global",
        label: "Global media",
        layer: { kind: "global" },
      });
    }
    return out;
  }, [scope.premiseId, scope.companyId]);

  const [active, setActive] = useState(0);
  // A scope change can shrink the tab list under a stale index.
  const index = Math.min(active, tabs.length - 1);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="assets-picker"
    >
      <DialogTitle sx={{ pb: 0 }}>Choose media</DialogTitle>
      {tabs.length > 1 && (
        <Tabs
          value={index}
          onChange={(_, v: number) => setActive(v)}
          sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
          data-testid="picker-layer-tabs"
        >
          {tabs.map((t) => (
            <Tab
              key={t.key}
              label={t.label}
              data-testid={`picker-tab-${t.key}`}
            />
          ))}
        </Tabs>
      )}
      <DialogContent dividers>
        <MediaGallery
          // Remount on tab change so folder navigation starts at that layer's
          // root rather than inside a path the new layer may not even have.
          key={tabs[index]!.key}
          layer={tabs[index]!.layer}
          scrollContain
          onSelect={(url, asset) => {
            onSelect(url, asset);
            onClose();
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
